import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { DataTable, columnTypes as colType, pitchType, pitchResult } from 'yankees-react-utils';
import VideoPlayerButton from '../../../components/VideoPlayerButton';
import StatTooltip from '../../../components/StatTooltip';
import usePlayerSettings from '../../../utils/usePlayerSettings';

const HitterTable = ({ pitches, isInBats }) => {
    const { t: translate } = useTranslation();
    const { ShowGameScore } = usePlayerSettings();

    if (!pitches || pitches.length === 0) {
        return (<div>{translate('sections.post-game.hitter.no-pitches')}</div>);
    }
    const rows = pitches.map((t) => {
        const videoparams = { gameid: t.gameid, eventseq: t.eventseq, pitchseq: t.pitchseq };
        const videoplaylist = isInBats ? (<VideoPlayerButton {...videoparams} />) : '-';
        const result = (t.islastpitch ? t.batvspitresult : (t.resultdescription || pitchResult(t.result, false, translate)));
        return { ...t, videoplaylist, result };
    });
    const playerparams = { gameid: pitches[0].gameid, eventseq: pitches[0].eventseq, batterid: pitches[0].batterid, pitcherid: pitches[0].pitcherid };
    const columns = [
        {
            id: 'videoplaylist',
            title: pitches.length < 0 ? (<VideoPlayerButton {...playerparams} />) : '',
            type: colType.NODE,
        },
        {
            id: 'number',
            title: '#',
            transform: (row) => row.abpitchseq,
            type: colType.NUMBER,
        },
        {
            id: 'count',
            title: translate('baseball.count'),
            transform: (row) => `${row.balls}-${row.strikes}`,
            type: colType.TEXT,
        },
        {
            id: 'pitchtype',
            title: translate('sections.post-game.hitter.type'),
            transform: (row) => pitchType(row.pitchtype, true, undefined, translate),
            type: colType.TEXT,
        },
        {
            id: 'velo',
            title: translate('sections.post-game.hitter.velocity'),
            transform: (row) => row.velocity,
            precision: 1,
        },
        ...(ShowGameScore ? [{
            id: 'swdecgrade',
            title: translate('baseball.abbr-swingdecision'),
            statistic: 'SwDecGrd',
            transform: (row) => (row.swdecgrade === null || row.swdecgrade === undefined ? '--' : row.swdecgrade),
            precision: 1,
        },
        {
            id: 'msgrade',
            title: translate('sections.post-game.hitter.abbr-ms-grade'),
            statistic: 'ConGrd',
            transform: (row) => (row.msgrade === null || row.msgrade === undefined ? '--' : row.msgrade),
            precision: 1,
        },
        {
            id: 'cqgrade',
            title: translate('baseball.abbr-contactquality'),
            statistic: 'CQGrd',
            transform: (row) => (row.cqgrade === null || row.cqgrade === undefined ? '--' : row.cqgrade),
            precision: 1,
        },
        {
            id: 'totalgrade',
            title: translate('common.total'),
            statistic: 'GSGrd',
            transform: (row) => (row.totalgrade === null || row.totalgrade === undefined ? '--' : row.totalgrade),
            precision: 1,
        }] : []),
        {
            id: 'result',
            title: translate('common.result'),
            type: colType.TEXT,
            cellClass: (row) => (row.islastpitch ? 'bold' : ''),
        },
    ];
    return (<DataTable columns={columns} rows={rows} statTooltip={StatTooltip} />);
};

HitterTable.propTypes = {
    pitches: PropTypes.arrayOf(PropTypes.shape({
        gameid: PropTypes.number,
        eventseq: PropTypes.number,
        pitchseq: PropTypes.number,
        batterid: PropTypes.number,
        pitcherid: PropTypes.number,
        abpitchseq: PropTypes.number,
        balls: PropTypes.number,
        strikes: PropTypes.number,
        pitchtype: PropTypes.number,
        velocity: PropTypes.number,
        result: PropTypes.number,
        resultdescription: PropTypes.string,
        islastpitch: PropTypes.bool,
        batvspitresult: PropTypes.string,
        takescore: PropTypes.number,
        pswing: PropTypes.number,
    })).isRequired,
    isInBats: PropTypes.bool.isRequired,
};

export default HitterTable;
